import React, { useEffect, useState } from "react";
import {
  NavigationContainer,
  useNavigationContainerRef,
} from "@react-navigation/native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import * as Linking from "expo-linking";
import { AppScreens } from "../screens/Utility/AppScreens";
import { regex, regexForSuccess } from "../utility/Index";
import {
  getLoginData,
  storeEventId,
  storeSessionId,
} from "../utility/AppStoreData";
import { TooltipProvider } from "../context/WalkthroughContext";
import { deepLinkingConfig } from "./config/DeepLinkingConfig";
import { validateRouteAccess } from "../utility/RouteProtectionConfig";

// Import screens
import ChooseGameLikeScreen from "../screens/Auth/ChooseGameLikeScreen";
import LoginWebScreen from "../screens/Auth/WebScreens/LoginWebScreen";
import SignupWebScreen from "../screens/Auth/WebScreens/SignupWebScreen";
import ResetSetPasswordWebScreen from "../screens/Auth/WebScreens/ResetSetPasswordWebScreen";
import ResetPasswordWebScreen from "../screens/Auth/WebScreens/ResetPasswordWebScreen";
import EmailConfirmationWebScreen from "../screens/Auth/WebScreens/EmailConfirmationWebScreen";
import DisputeCenter from "../screens/Auth/WebScreens/DisputeCenter";
import GameShowPreviewScreen from "../screens/Auth/WebScreens/GameShowPreviewScreen";
import HomeWebScreenStatic from "../screens/Auth/WebScreens/HomeWebScreenStatic";
import SetupScreen from "../screens/Auth/WebScreens/SetupScreen";
import ChooseAvatarScreen from "../screens/Auth/ChooseAvatarScreen";
import DrawerRoute from "./DrawerRoute";
import EventTypeScreen from "../screens/Auth/Event/EventTypeScreen";
import ShortUrlRedirectScreen from "../screens/Auth/WebScreens/ShortUrlRedirectScreen";
import SimpleShortUrlRedirectScreen from "../screens/Auth/WebScreens/SimpleShortUrlRedirectScreen";
import {
  PaymentSuccess,
  PaymentFailed,
  PaymentCancel,
} from "../screens/Auth/WebScreens/Payment";

const Stack = createNativeStackNavigator();

// Capture query params immediately before React Navigation can strip them
let initialSearchParams = "";
if (typeof window !== "undefined") {
  initialSearchParams = window.location.search;
}

const Route = (initialScreen) => {
  const [userLogin, setUserLogin] = useState(false);
  const navigationRef = useNavigationContainerRef();

  useEffect(() => {
    const handleInitialUrl = async () => {
      let initialUrl = await Linking.getInitialURL();

      // Web fallback: Check global capture if Linking failed or URL looks clean
      if (typeof window !== "undefined") {
        const currentUrl = window.location.href;
        // If we captured params globally but they are gone from current URL, append them back for processing
        if (initialSearchParams && !currentUrl.includes("refreshToken=")) {
          const baseUrl = currentUrl.split("?")[0];
          initialUrl = `${baseUrl}${initialSearchParams}`;
        } else if (!initialUrl) {
          initialUrl = currentUrl;
        }
      }

      if (initialUrl) {
        // Run auto-login logic
        const processedUrl = await handleAutoLogin(initialUrl);

        if (regex.test(initialUrl)) {
          const url = new URL(initialUrl);
          const id = url.searchParams.get("ID");
          storeEventId(id);
        } else if (regexForSuccess.test(initialUrl)) {
          const url = new URL(initialUrl);
          const session_id = url.searchParams.get("session_id");
          if (session_id) {
            storeSessionId(session_id);
          }
        }
      }
    };

    handleInitialUrl();
  }, []);

  useEffect(() => {
    loadData();
  }, [userLogin]);

  const checkAuthAndRedirect = async (url) => {
    if (!url) return url;

    try {
      const loginData = await getLoginData();
      const isLoggedIn = loginData !== null;

      // Extract the path after the domain
      const urlObj = new URL(url);
      const path = urlObj.pathname;

      console.log(
        "🔍 [Route] Checking auth for path:",
        path,
        "isLoggedIn:",
        isLoggedIn
      );

      // Use centralized route protection config
      const validation = validateRouteAccess(path, isLoggedIn);

      if (!validation.allowed) {
        const pathWithQuery = urlObj.pathname + urlObj.search;
        console.log("🔒 [Route] Access denied:", validation.reason);
        console.log("📍 [Route] Original path:", pathWithQuery);
        console.log("🔀 [Route] Redirecting to:", validation.redirectTo);

        // Return modified URL to redirect to SetupScreen with original path preserved
        return `${urlObj.origin}${
          validation.redirectTo ||
          `/auth/setup?path=${encodeURIComponent(pathWithQuery)}`
        }`;
      }

      console.log("✅ [Route] Access granted for:", path);
      return url;
    } catch (error) {
      console.error("❌ [Route] Error in checkAuthAndRedirect:", error);
      return url;
    }
  };

  // Shared auto-login logic
  const handleAutoLogin = async (url) => {
    if (!url || (!url.includes("refreshToken=") && !url.includes("token="))) {
      return url;
    }

    try {
      const urlObj = new URL(url);
      const queryParams = urlObj.searchParams;
      const refreshToken =
        queryParams.get("refreshToken") || queryParams.get("token");

      if (refreshToken) {
        console.log("🔄 [Route] Auto-login attempt with token");

        // Check if already logged in
        const currentLoginData = await getLoginData();
        if (currentLoginData) {
          console.log("✅ [Route] Already logged in, skipping auto-login");
        } else {
          // Import getUserProfile dynamically
          const { getUserProfile } = require("../services/authAPI");
          const { storeLoginData } = require("../utility/AppStoreData");

          // For auto-login, we assume the passed token is an Access Token that can be used to fetch the profile
          const response = await getUserProfile(refreshToken);

          // User change: statusCode vs status (keeping statusCode as requested)
          if (response && response.statusCode === 200 && response.body) {
            // Construct userData to match standard login response structure
            const userData = {
              username: response.body.userID,
              ...response.body, // Contains profile data
              AccessToken: refreshToken, // Map passed token as AccessToken
              accessToken: refreshToken, // Legacy support
              // If backend returns a new IdToken/RefreshToken in profile response, use it.
              // Otherwise, we might lack IdToken/RefreshToken unless we treat the passed token as such.
              ...(response.body.AccessToken
                ? { AccessToken: response.body.AccessToken }
                : {}),
              ...(response.body.IdToken
                ? { IdToken: response.body.IdToken }
                : {}),
              ...(response.body.RefreshToken
                ? { RefreshToken: response.body.RefreshToken }
                : {}),
              // Ensure lowercase versions are also present if needed
              ...(response.body.accessToken
                ? { accessToken: response.body.accessToken }
                : {}),
              ...(response.body.refreshToken
                ? { refreshToken: response.body.refreshToken }
                : {}),
            };


            await storeLoginData(userData);
            if (response.body.userID) {
              await AsyncStorage.setItem("username", response.body.userID);
            }


            setUserLogin(true);
            console.log("✅ [Route] Auto-login successful");

            // Clean URL in address bar without reloading
            if (typeof window !== "undefined") {
              const urlObj = new URL(url);
              urlObj.searchParams.delete("refreshToken");
              urlObj.searchParams.delete("token");
              window.history.replaceState(null, "", urlObj.toString());
            }

            // Revert: Re-add window.location.reload() as user requested "revert removed loop"
            if (typeof window !== "undefined") {
              window.location.reload();
            }

            // Navigate to Dashboard (Might be unreachable due to reload)
            if (navigationRef.isReady()) {
              navigationRef.navigate(AppScreens.DrawerRoute);
            }
          } else {
            console.warn(
              "⚠️ [Route] Auto-login failed: Invalid token or profile fetch failed"
            );
          }
        }

        // Return clean URL
        if (typeof window !== "undefined") {
          return window.location.href;
        }
        return urlObj.toString();
      }
    } catch (error) {
      console.error("❌ [Route] Auto-login error:", error);
    }
    return url;
  };

  return (
    <TooltipProvider>
      <NavigationContainer
        ref={navigationRef}
        linking={{
          config: deepLinkingConfig,
          async getInitialURL() {
            let url = await Linking.getInitialURL();
            if (url) {
              // Also attempt auto-login here for deep links
              url = await handleAutoLogin(url);
              return await checkAuthAndRedirect(url);
            }
            return url;
          },
          subscribe(listener) {
            const onReceiveURL = async ({ url }) => {
              let processedUrl = url;
              if (url) {
                processedUrl = await handleAutoLogin(url);
              }
              const redirectedUrl = await checkAuthAndRedirect(processedUrl);
              listener(redirectedUrl);
            };

            const subscription = Linking.addEventListener("url", onReceiveURL);
            return () => {
              subscription.remove();
            };
          },
        }}
      >
        <Stack.Navigator
          screenOptions={{ headerShown: false, gestureEnabled: true }}
          initialRouteName={AppScreens.LoginWebScreen}
        >
          {/* Screen redirect ma issue aave che Stack ma*/}
          {/* <Stack.Screen name={AppScreens.DrawerRoute} component={DrawerRoute} /> */}
          <Stack.Screen name="WebScreenStack" component={WebScreenStack} />
        </Stack.Navigator>
      </NavigationContainer>
    </TooltipProvider>
  );

  function WebScreenStack() {
    return (
      <Stack.Navigator
        screenOptions={{ headerShown: false }}
        initialRouteName={
          userLogin ? AppScreens.DrawerRoute : AppScreens.LoginWebScreen
        }
        // initialRouteName={AppScreens.HomeWebScreen}
      >
        <Stack.Screen
          name={AppScreens.LoginWebScreen}
          component={LoginWebScreen}
        />
        <Stack.Screen
          name={AppScreens.HomeWebScreenStatic}
          component={HomeWebScreenStatic}
        />
        <Stack.Screen name={AppScreens.SetupScreen} component={SetupScreen} />
        <Stack.Screen
          name={AppScreens.ChooseGameLikeScreen}
          component={ChooseGameLikeScreen}
        />
        <Stack.Screen
          name={AppScreens.ChooseAvatarScreen}
          component={ChooseAvatarScreen}
        />
        <Stack.Screen
          name={AppScreens.SignupWebScreen}
          component={SignupWebScreen}
        />
        <Stack.Screen
          name={AppScreens.EmailConfirmationWebScreen}
          component={EmailConfirmationWebScreen}
        />
        <Stack.Screen
          name={AppScreens.ResetPasswordWebScreen}
          component={ResetPasswordWebScreen}
        />
        <Stack.Screen
          name={AppScreens.ResetSetPasswordWebScreen}
          component={ResetSetPasswordWebScreen}
        />
        <Stack.Screen
          name={AppScreens.GameShowPreviewScreen}
          component={GameShowPreviewScreen}
        />
        <Stack.Screen
          name="ShortUrlRedirectScreen"
          component={ShortUrlRedirectScreen}
        />
        <Stack.Screen
          name="SimpleShortUrlRedirectScreen"
          component={SimpleShortUrlRedirectScreen}
          options={{ title: "Redirecting..." }}
        />
        <Stack.Screen
          name={AppScreens.DisputeCenter}
          component={DisputeCenter}
        />

        <Stack.Screen name={AppScreens.DrawerRoute} component={DrawerRoute} />
        <Stack.Screen
          name={AppScreens.EventTypeScreen}
          component={EventTypeScreen}
        />
        <Stack.Screen
          name={AppScreens.PaymentSuccess}
          component={PaymentSuccess}
        />
        <Stack.Screen
          name={AppScreens.PaymentFailed}
          component={PaymentFailed}
        />
        <Stack.Screen
          name={AppScreens.PaymentCancel}
          component={PaymentCancel}
        />
      </Stack.Navigator>
    );
  }

  async function loadData() {
    const data = await getLoginData();
    if (data != null) {
      setUserLogin(true);
    } else {
      setUserLogin(false);
    }
  }
};

export default Route;
